/**
 * Microsoft Clarity must never record extra-payments/warranty/vacations (GDPR/privacy).
 * Clarity has no supported pause/resume API - consent() only controls cookies, not the
 * underlying /collect uploads - so this module is the centralized point that:
 *  - never lets Clarity load at all on a fresh page load that lands directly on one of
 *    these routes (see the matching guard in public/index.html, which is what actually
 *    prevents that case - keep the two excluded-prefix lists in sync, a raw HTML
 *    <script> can't import this module).
 *  - for in-app navigation, where Clarity may already be running, tears down its
 *    script/global and blocks its outbound network calls while on an excluded route.
 *    The network guard is the real enforcement layer: it operates below Clarity's own
 *    code, so it doesn't depend on being able to fully uninstall a live SDK instance
 *    (removing a <script> tag or deleting window.clarity can't retract listeners/
 *    observers it already registered against window/document - see the plan's
 *    limitations section for the residual risk this leaves for in-app navigation).
 */
export const CLARITY_EXCLUDED_PATH_PREFIXES = ["/extra-payments", "/warranty", "/vacations"];

const CLARITY_PROJECT_ID = "ycipg8ssn3";
const CLARITY_SCRIPT_SELECTOR = 'script[src^="https://www.clarity.ms/tag/"]';
const CLARITY_HOST_PATTERN = /^https?:\/\/([a-z0-9-]+\.)*clarity\.ms\b/i;

export function isClarityExcludedPath(pathname: string): boolean {
    return CLARITY_EXCLUDED_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

function isClarityUrl(url: string): boolean {
    return CLARITY_HOST_PATTERN.test(url);
}

function loadClarity(): void {
    if (document.querySelector(CLARITY_SCRIPT_SELECTOR)) return; // already loaded

    (function (c: any, l: Document, a: string, r: string, i: string) {
        c[a] = c[a] || function (...args: unknown[]) { (c[a].q = c[a].q || []).push(args); };
        const t: any = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        const y = l.getElementsByTagName(r)[0];
        y.parentNode?.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
}

function removeClarity(): void {
    document.querySelectorAll(CLARITY_SCRIPT_SELECTOR).forEach(node => node.remove());
    delete (window as any).clarity;
}

let isRouteExcluded = false;

function installClarityNetworkGuard(): void {
    const originalFetch = window.fetch?.bind(window);
    if (originalFetch) {
        window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
            const url = typeof input === "string" ? input : (input as Request).url ?? String(input);
            if (isRouteExcluded && isClarityUrl(url)) {
                return Promise.resolve(new Response(null, { status: 204 }));
            }
            return originalFetch(input as any, init);
        };
    }

    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;
    // XHR's `open` is overloaded (with optional async/username/password params); cast
    // the replacement to bypass strict overload-assignability checks, same pattern as
    // any other prototype monkey-patch.
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: any[]) {
        (this as any).__clarityGuardUrl = String(url);
        return originalXhrOpen.apply(this, [method, url, ...rest] as any);
    } as any;
    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, ...args: any[]) {
        const url = (this as any).__clarityGuardUrl;
        if (isRouteExcluded && url && isClarityUrl(url)) return;
        return originalXhrSend.apply(this, args as any);
    };

    if (navigator.sendBeacon) {
        const originalSendBeacon = navigator.sendBeacon.bind(navigator);
        navigator.sendBeacon = (url: string | URL, data?: BodyInit) => {
            if (isRouteExcluded && isClarityUrl(String(url))) return true;
            return originalSendBeacon(url as any, data);
        };
    }
}

// Install as early as possible - as a side effect of first importing this module,
// before the app can possibly navigate anywhere.
installClarityNetworkGuard();

/**
 * Call on every route change (App.tsx). Only acts on an actual excluded/allowed
 * transition - safe to call on every render/navigation.
 */
export function syncClarityWithRoute(pathname: string): void {
    const excluded = isClarityExcludedPath(pathname);
    if (excluded === isRouteExcluded) return;

    isRouteExcluded = excluded;
    if (excluded) {
        removeClarity();
    } else {
        loadClarity();
    }
}
